import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
import moment from 'moment';

import Conditions from './CellRenderers/Conditions';
import { getAllConditions, getSimpleConditions } from '../../api/conditions';
import { excelStyles } from '../../components/Grid/ExcelStyles/excelStyles';
import LoadRowsComponent from '../../helpers/LoadRowsComponent';
import StudyLinkArray from './CellRenderers/StudyLinkArray';
import TotalRow from '../../components/Grid/CellRenderer/TotalRow';
import { toaster } from '../../helpers/toaster';

const ConditionsGrid = ({
  conditionList,
  search,
  onRowClicked,
  onDeleteClicked,
  setRefresh,
  refresh,
  sort = "",
  setSort = () => {},
}) => {
  const [rowData, setRowData] = useState(conditionList);
  const [gridApi, setGridApi] = useState(null);
  const [totalRows, setTotalRows] = useState(conditionList ? conditionList.length : 0);
  const conditionsSimpleList = useRef(null);

  useEffect(() => {
    getSimpleConditions().then(res => {
      if (res && res.length) {
        let conditions = res.map(item => ({
          value: item.id,
          label: item.name,
        }));
        conditionsSimpleList.current = conditions;
      }else {
        // setInterventionSimpleList(res);
      }
    }).catch(error => {
      toaster("Failed to get condition list", "error")
    });
  }, [])

  useEffect(() => {
    if(refresh){
      let refreshOptions = {}
      // if adding the first row, purge for item to properly refresh
      if (gridApi.getDisplayedRowCount() === 0) refreshOptions = { purge: true };
      gridApi.refreshServerSideStore(refreshOptions);
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if(gridApi){
      //loads in more rows while scrolling
      const dataSource = {
        getRows: function(params) {
          let sortParam = "";
          if(params.request.sortModel.length !== 0){
            const sortColumn = params.request.sortModel[0].colId;
            const sortWay = params.request.sortModel[0].sort;
            if(sortWay === "desc"){
              sortParam += "-";
            }
            sortParam += sortColumn;
            sortParam = sortParam.replaceAll(".", "__");
          }
          if(sortParam){
            setSort(sortParam);
          }
          else{
            setSort("");
          }
          const rows = getAllConditions({limit: 100, offset: params?.request.startRow, search: search, sort: sort})
            .then( res => {
              const items = res.results?.map(item => ({
                      ...item,
                      // synonyms__name: (item.synonyms || []),
                      synonyms__name: (item.synonyms || []).slice(0, 5).join(", "),
                      studies__org_study_id: (item.studies || []).map(ele => {return ({org_study_id: ele.org_study_id, id: ele.id, uid: ele.uid})}).slice(0, 2),
                      root_condition__name: item.root_condition?.name
                    }));
                    setTotalRows(res.count);
                    params.success({
                      rowData: items,
                      rowCount: res.count
                    })
                    if(res.count === 0){
                      gridApi.showNoRowsOverlay();
                    }

            });
        }
      };
      gridApi.setServerSideDatasource(dataSource);
    }
  }, [gridApi, search, sort]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const cellClicked = (event) => {
    const { cellRenderer } = event.colDef;
    if (cellRenderer !== "removeRow") {
      onRowClicked(event.data);
    }
  };

  const deleteRow = (itemId, itemName) => {
    onDeleteClicked(itemId, itemName);
  };

  const onFilterChanged = () => {
    if (gridApi) {
      const displayedRowCount = gridApi.getDisplayedRowCount();
      setTotalRows(displayedRowCount);
    }
  };

  const gridOptions = {
    columnDefs: [
      // {
      //   headerName: 'Id',
      //   width: 50,
      //   field: 'id',
      // },
      {
        headerName: 'Condition',
        field: 'name',
        flex: '2'
      },
      {
        headerName: 'Studies',
        field: 'studies__org_study_id',
        sortable: false,
        cellRenderer: "studyLinkArray",
        flex: '1'
      },
      {
        headerName: 'Synonyms',
        field: 'synonyms__name',
        cellRenderer: "conditions",
        // cellRenderer: "conditionsSynonyms",
        sortable: false,
        flex: '1'
      },
      {
        headerName: 'Root Condition',
        field: 'root_condition__name',
        // cellRenderer: "conditions",
        // cellRenderer: "conditionsSynonyms",
        sortable: false,
        flex: '1'
      },

      {
        headerName: 'Updated at',
        cellRenderer: "updatedTime",
        field: "updated_at",
        flex: '1'
      },
      {
        headerName: 'Created at',
        cellRenderer: "createdTime",
        field: "created_at",
        flex: '1'
      },
      {
        headerName: "",
        width: 40,
        cellRenderer: "removeRow",
      }
    ],
    defaultColDef: {
      resizable: true,
      sortable: true,
      wrapText: true,
      filter: true,
      enablePivot: true,
      enableCharts: false,
      enableRowGroup: true,
      filterParams: {
        clearButton: true
      },
      cellStyle: { "white-space": "normal !important" },
      autoHeight: true,
      cellClassRules: {
        oddBackground: function (params) {
          return params.rowIndex % 2 == 0;
        },
      },
    },
    excelStyles,
    frameworkComponents: {
      conditions: Conditions,
      studyLinkArray: StudyLinkArray,
      conditionsSynonyms: ({data}) => {
        if(data?.synonyms__name){
          let synonymListString = ''
          for(let x = 0; x < data?.synonyms__name.length; x++){
            let found = conditionsSimpleList.current.find((element) => element.value === data?.synonyms__name[x])
            if(found){
              synonymListString += found.label
              if(x !== data?.synonyms__name.length - 1){
                synonymListString += ', '
              }
            }
          }
          return(synonymListString)
        }
      },
      removeRow: ({ data }) => {
        const id = data?.id;
        const name = data?.name;
        return (
          <i
            className="fal fa-trash-alt"
            style={{ color: "red" }}
            onClick={() => deleteRow(id, name)}
          ></i>
        );
      },
      updatedTime: ({ data }) => {
        const updated_at = data?.updated_at;
        return (
          <span>{ updated_at && moment(updated_at).format("DD-MMM-YYYY, HH:mm")}</span>
        );
      },  
      createdTime: ({ data }) => {
        const created_at = data?.created_at;
        return (
          <span>{ created_at && moment(created_at).format("DD-MMM-YYYY, HH:mm")}</span>
        );
      },  
      LoadRowsComponent: LoadRowsComponent,
    },
    loadingCellRenderer: 'LoadRowsComponent',
    loadingCellRendererParams :{
      loadingMessage: 'One moment please...',
    },
  };

  return (
    <Container className="ag-theme-balham">
      <AgGridReact
        onGridReady={onGridReady}
        gridOptions={gridOptions}
        onCellClicked={event => cellClicked(event)}
        onFilterChanged={onFilterChanged}
        // rowData={rowData}
        rowModelType={'serverSide'}
        serverSideStoreType={'partial'}
        cacheBlockSize={100}
      ></AgGridReact>
      <TotalRow count={totalRows} />
    </Container>
  );
};

export default ConditionsGrid;

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding-bottom: 20px;
`
